/**
 * Emite Excel de packing list inventario:
 * - Sin ganancia: tabla "Medidas con NF de proveedor" + resúmenes.
 * - Con ganancia: tabla proveedor + tabla BL/NF; resúmenes superiores en M³ proveedor;
 *   tabla por Pcs/paq usa M³ declarado (ganancia).
 * La primera tabla mantiene Container numérico para import invoice (parsePackingListExcel).
 */
import ExcelJS from 'exceljs';

const DETAIL_HEADERS = [
  'Container',
  'Proveedor',
  'BL',
  'GRADO',
  'ALMACEN',
  'NF',
  'Paquetes',
  'Pcs/paq',
  'Total pcs',
  'Espesor',
  'Ancho',
  'Largo',
  'Volumen',
  'Peso',
  'Dimensión',
  'Proveedor usd/m3',
  'Total usd',
  'TC',
  'Precio (R$/m3)',
  'Total (R$)',
];

function num(v) {
  if (v == null || v === '') return null;
  const n = Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

function dimStr(esp, anc, lar) {
  const parts = [esp, anc, lar].map((x) => (x == null || x === '' ? '' : String(x).replace('.', ',')));
  if (!parts[0] && !parts[1] && !parts[2]) return '';
  return `${parts[0]}x${parts[1]}x${parts[2]}`;
}

function fmtDec(v, dec = 2) {
  const n = num(v);
  if (n == null) return '';
  return n.toLocaleString('es-CL', {
    minimumFractionDigits: dec,
    maximumFractionDigits: dec,
  });
}

const THIN = { style: 'thin', color: { argb: 'FF000000' } };
const BORDER = { top: THIN, left: THIN, bottom: THIN, right: THIN };

const FILL_HEADER_DARK = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FF548235' },
};
const FILL_HEADER_LIGHT = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFC6E0B4' },
};
const FILL_SUBTOTAL = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFE2EFDA' },
};
const FILL_GROUP = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFC6E0B4' },
};

function styleRange(ws, r1, c1, r2, c2, style) {
  for (let r = r1; r <= r2; r += 1) {
    for (let c = c1; c <= c2; c += 1) {
      const cell = ws.getCell(r, c);
      if (style.border) cell.border = style.border;
      if (style.fill) cell.fill = style.fill;
      if (style.font) cell.font = { ...cell.font, ...style.font };
      if (style.alignment) cell.alignment = { ...cell.alignment, ...style.alignment };
    }
  }
}

function writeRow(ws, rowIndex, values, opts = {}) {
  const row = ws.getRow(rowIndex);
  values.forEach((v, i) => {
    const cell = row.getCell(i + 1);
    cell.value = v === '' || v === undefined ? null : v;
    if (opts.border) cell.border = BORDER;
    if (opts.font) cell.font = { ...cell.font, ...opts.font };
    if (opts.fill) cell.fill = opts.fill;
    if (opts.align) cell.alignment = opts.align;
  });
  if (opts.height) row.height = opts.height;
  return row;
}

function downloadWorkbook(buffer, fname) {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fname;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function pesoByContenedor(lineas) {
  const pesoByCont = new Map();
  lineas.forEach((ln) => {
    const c = Number(ln.correlativo_contenedor) || 1;
    if (!pesoByCont.has(c)) pesoByCont.set(c, num(ln.peso_mercaderia) || 0);
  });
  return pesoByCont;
}

function medidasFromLinea(ln, mode) {
  if (mode === 'ganancia') {
    return {
      esp: ln.espesor_ganancia ?? ln.espesor,
      anc: ln.ancho_ganancia ?? ln.ancho,
      lar: ln.largo_ganancia ?? ln.largo,
      m3: num(ln.total_m3_ganancia),
    };
  }
  return {
    esp: ln.espesor,
    anc: ln.ancho,
    lar: ln.largo,
    m3: num(ln.total_m3),
  };
}

/** Filas de detalle (proveedor o ganancia). */
function buildDetailRows(lineas, { mode, icb, pesoByCont }) {
  const pesoWritten = new Set();
  return lineas.map((ln) => {
    const cont = Number(ln.correlativo_contenedor) || 1;
    const { esp, anc, lar, m3 } = medidasFromLinea(ln, mode);
    const usdM3 = num(ln.precio_usd_m3) ?? icb;
    const totalUsd = usdM3 != null && m3 != null
      ? usdM3 * m3
      : mode === 'ganancia'
        ? null
        : num(ln.total_usd);
    const espN = num(esp);
    const ancN = num(anc);
    const larN = num(lar);
    let peso = '';
    if (!pesoWritten.has(cont)) {
      pesoWritten.add(cont);
      const p = pesoByCont.get(cont);
      peso = p != null && p !== 0 ? p : num(ln.peso_mercaderia) ?? '';
    }
    return [
      cont,
      ln.proveedor ?? '',
      '',
      ln.grado ?? '',
      ln.almacen ?? '',
      ln.nro_nf ?? '',
      num(ln.paquetes) ?? '',
      num(ln.pcs_paq) ?? '',
      num(ln.total_pcs) ?? '',
      espN != null ? espN : esp ?? '',
      ancN != null ? ancN : anc ?? '',
      larN != null ? larN : lar ?? '',
      m3 != null ? Number(m3.toFixed(3)) : '',
      peso,
      dimStr(esp, anc, lar),
      usdM3 != null ? Number(usdM3.toFixed(2)) : '',
      totalUsd != null ? Number(totalUsd.toFixed(2)) : '',
      '',
      '',
      '',
    ];
  });
}

const contLabel = (n) => `Container ${n}`;

function buildAggregates(detailRows, lineas, pesoByCont) {
  const sumPaq = lineas.reduce((s, l) => s + (num(l.paquetes) || 0), 0);
  const sumPcs = lineas.reduce((s, l) => s + (num(l.total_pcs) || 0), 0);
  const sumVol = detailRows.reduce((s, r) => s + (num(r[12]) || 0), 0);
  const sumPeso = [...pesoByCont.values()].reduce((a, b) => a + b, 0);
  const sumUsd = detailRows.reduce((s, r) => s + (num(r[16]) || 0), 0);

  const groupMap = new Map();
  detailRows.forEach((row) => {
    const contNum = row[0];
    const key = `${contNum}|${row[14]}|${row[15]}`;
    let g = groupMap.get(key);
    if (!g) {
      g = {
        container: contLabel(contNum),
        contNum,
        dim: row[14],
        usdM3: row[15],
        paq: 0,
        pcs: 0,
        vol: 0,
        peso: 0,
        totalUsd: 0,
      };
      groupMap.set(key, g);
    }
    g.paq += num(row[6]) || 0;
    g.pcs += num(row[8]) || 0;
    g.vol += num(row[12]) || 0;
    g.totalUsd += num(row[16]) || 0;
  });
  for (const g of groupMap.values()) {
    g.peso = pesoByCont.get(Number(g.contNum)) || 0;
  }

  const groupsByCont = new Map();
  for (const g of groupMap.values()) {
    const k = String(g.contNum);
    if (!groupsByCont.has(k)) groupsByCont.set(k, []);
    groupsByCont.get(k).push(g);
  }

  const groupPcs = new Map();
  detailRows.forEach((row) => {
    const contNum = row[0];
    const key = `${contNum}|${row[14]}|${row[7]}`;
    let g = groupPcs.get(key);
    if (!g) {
      g = {
        container: contLabel(contNum),
        contNum,
        dim: row[14],
        pcsPaq: row[7],
        paq: 0,
        pcs: 0,
        vol: 0,
        peso: 0,
        totalUsd: 0,
      };
      groupPcs.set(key, g);
    }
    g.paq += num(row[6]) || 0;
    g.pcs += num(row[8]) || 0;
    g.vol += num(row[12]) || 0;
    g.totalUsd += num(row[16]) || 0;
  });
  for (const g of groupPcs.values()) {
    g.peso = pesoByCont.get(Number(g.contNum)) || 0;
  }

  return { sumPaq, sumPcs, sumVol, sumPeso, sumUsd, groupsByCont, groupPcs };
}

function writeDetailTable(ws, startRow, title, detailRows) {
  let r = startRow;
  ws.getCell(r, 1).value = title;
  ws.getCell(r, 1).font = { bold: true, size: 14, name: 'Calibri' };
  r += 1;

  const headerRow = r;
  writeRow(ws, r, DETAIL_HEADERS, {
    border: true,
    font: { bold: true, size: 11, name: 'Calibri' },
    align: { horizontal: 'center', vertical: 'middle', wrapText: true },
  });
  styleRange(ws, r, 1, r, DETAIL_HEADERS.length, {
    border: BORDER,
    font: { bold: true, size: 11, name: 'Calibri', color: { argb: 'FFFFFFFF' } },
    fill: FILL_HEADER_DARK,
    alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
  });
  ws.getRow(r).height = 30;
  r += 1;

  detailRows.forEach((vals) => {
    writeRow(ws, r, vals, {
      border: true,
      font: { size: 10, name: 'Calibri' },
    });
    styleRange(ws, r, 1, r, DETAIL_HEADERS.length, { border: BORDER });
    r += 1;
  });

  return { nextRow: r, headerRow, dataEnd: r - 1 };
}

function writeSummaryBlocks(ws, startRow, detailRows, lineas, pesoByCont, detailRowsPcsPaq = null) {
  const aggMain = buildAggregates(detailRows, lineas, pesoByCont);
  const aggPcs = detailRowsPcsPaq
    ? buildAggregates(detailRowsPcsPaq, lineas, pesoByCont)
    : aggMain;
  const { sumPaq, sumPcs, sumVol, sumPeso, sumUsd, groupsByCont } = aggMain;
  const { groupPcs, sumVol: sumVolPcs, sumUsd: sumUsdPcs } = aggPcs;
  let r = startRow;

  const sumHeaders = ['Suma de Paquetes', 'Suma de Total pcs', 'Suma de Volumen', 'Suma de Peso'];
  const sumValues = [sumPaq, sumPcs, Number(sumVol.toFixed(3)), sumPeso];
  sumHeaders.forEach((h, i) => {
    const cell = ws.getCell(r, i + 1);
    cell.value = h;
    cell.font = { bold: true, size: 10, name: 'Calibri' };
    cell.fill = FILL_HEADER_LIGHT;
    cell.border = BORDER;
    cell.alignment = { horizontal: 'center', wrapText: true };
  });
  r += 1;
  sumValues.forEach((v, i) => {
    const cell = ws.getCell(r, i + 1);
    cell.value = v;
    cell.font = { bold: true, size: 10, name: 'Calibri' };
    cell.border = BORDER;
    cell.alignment = { horizontal: 'right' };
  });
  r += 2;

  const group2Headers = [
    'Container',
    'Dimensión',
    'Precio (R$/m3)',
    'Proveedor usd/m3',
    'Suma de Paquetes',
    'Suma de Total pcs',
    'Suma de Volumen',
    'Suma de Peso',
    'Suma de Total (R$)',
    'Suma de Total usd',
  ];
  writeRow(ws, r, group2Headers, { border: true });
  styleRange(ws, r, 1, r, group2Headers.length, {
    border: BORDER,
    fill: FILL_HEADER_DARK,
    font: { bold: true, size: 10, name: 'Calibri', color: { argb: 'FFFFFFFF' } },
    alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
  });
  ws.getRow(r).height = 28;
  r += 1;

  const sortedContKeys = [...groupsByCont.keys()].sort((a, b) => Number(a) - Number(b));
  for (const contKey of sortedContKeys) {
    const items = groupsByCont.get(contKey) || [];
    let tPaq = 0;
    let tPcs = 0;
    let tVol = 0;
    let tPeso = 0;
    let tUsd = 0;
    for (const g of items) {
      writeRow(
        ws,
        r,
        [
          g.container,
          g.dim,
          '',
          g.usdM3,
          g.paq,
          g.pcs,
          Number(g.vol.toFixed(3)),
          g.peso,
          '',
          Number(g.totalUsd.toFixed(2)),
        ],
        { border: true, font: { size: 10, name: 'Calibri' } }
      );
      styleRange(ws, r, 1, r, group2Headers.length, { border: BORDER });
      ws.getCell(r, 1).fill = FILL_GROUP;
      ws.getCell(r, 2).fill = FILL_GROUP;
      tPaq += g.paq;
      tPcs += g.pcs;
      tVol += g.vol;
      tPeso = g.peso;
      tUsd += g.totalUsd;
      r += 1;
    }
    writeRow(
      ws,
      r,
      [
        `Total ${contKey}`,
        '',
        '',
        '',
        tPaq,
        tPcs,
        Number(tVol.toFixed(3)),
        tPeso,
        '',
        Number(tUsd.toFixed(2)),
      ],
      { border: true, font: { bold: true, size: 10, name: 'Calibri' } }
    );
    styleRange(ws, r, 1, r, group2Headers.length, {
      border: BORDER,
      fill: FILL_SUBTOTAL,
      font: { bold: true, size: 10, name: 'Calibri' },
    });
    r += 1;
  }

  writeRow(
    ws,
    r,
    [
      'Total general',
      '',
      '',
      '',
      sumPaq,
      sumPcs,
      Number(sumVol.toFixed(3)),
      sumPeso,
      '',
      Number(sumUsd.toFixed(2)),
    ],
    { border: true, font: { bold: true, size: 10, name: 'Calibri' } }
  );
  styleRange(ws, r, 1, r, group2Headers.length, {
    border: BORDER,
    font: { bold: true, size: 10, name: 'Calibri' },
  });
  r += 2;

  const group3Headers = [
    'Container',
    'Dimensión',
    'Pcs/paq',
    'Suma de Paquetes',
    'Suma de Total pcs',
    'Suma de Volumen',
    'Suma de Peso',
    'Suma de Total usd',
  ];
  writeRow(ws, r, group3Headers, { border: true });
  styleRange(ws, r, 1, r, group3Headers.length, {
    border: BORDER,
    fill: FILL_HEADER_DARK,
    font: { bold: true, size: 10, name: 'Calibri', color: { argb: 'FFFFFFFF' } },
    alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
  });
  ws.getRow(r).height = 28;
  r += 1;

  const pcsList = [...groupPcs.values()].sort((a, b) => {
    const ca = Number(a.contNum) - Number(b.contNum);
    if (ca !== 0) return ca;
    return String(a.dim).localeCompare(String(b.dim));
  });
  for (const g of pcsList) {
    writeRow(
      ws,
      r,
      [
        g.container,
        g.dim,
        g.pcsPaq,
        g.paq,
        g.pcs,
        Number(g.vol.toFixed(3)),
        g.peso,
        Number(g.totalUsd.toFixed(2)),
      ],
      { border: true, font: { size: 10, name: 'Calibri' } }
    );
    styleRange(ws, r, 1, r, group3Headers.length, { border: BORDER });
    ws.getCell(r, 1).fill = FILL_GROUP;
    ws.getCell(r, 2).fill = FILL_GROUP;
    r += 1;
  }
  writeRow(
    ws,
    r,
    [
      'Total general',
      '',
      '',
      sumPaq,
      sumPcs,
      Number(sumVolPcs.toFixed(3)),
      sumPeso,
      Number(sumUsdPcs.toFixed(2)),
    ],
    { border: true, font: { bold: true, size: 10, name: 'Calibri' } }
  );
  styleRange(ws, r, 1, r, group3Headers.length, {
    border: BORDER,
    font: { bold: true, size: 10, name: 'Calibri' },
  });

  return r + 1;
}

/** Nombre de archivo: PACKING+ NF BK SAO600483200 V2.xlsx */
export function packingListExcelFileName(pl, draftLabel = null) {
  const raw = String(pl?.booking ?? '').trim() || String(pl?.nombre ?? '').trim();
  const sinBk = raw.replace(/^BK\s+/i, '').trim().replace(/\s+V\d+$/i, '').trim();
  const codigo = (sinBk || 'PackingList').toUpperCase();
  const draft = draftFileSuffix(draftLabel);
  const ver = Number(pl?.version) || 1;
  const verSuffix = ver > 1 ? ` V${ver}` : '';
  return `PACKING+ NF BK ${codigo}${draft ? ` ${draft}` : ''}${verSuffix}.xlsx`;
}

function normalizeDraftLabel(label) {
  if (label == null || label === '') return '';
  const s = String(label).trim();
  const legacy = s.match(/^Draft\s+0*(\d+)$/i);
  if (legacy) return String(Number(legacy[1]));
  return s;
}

function draftFileSuffix(label) {
  const draft = normalizeDraftLabel(label);
  if (!draft) return '';
  const n = Number(draft);
  if (Number.isFinite(n) && n > 0) return `Draft ${String(n).padStart(2, '0')}`;
  return `Draft ${draft}`;
}

function draftOptions(cantidad) {
  const n = Math.max(1, Math.min(20, Number(cantidad) || 1));
  return Array.from({ length: n }, (_, i) => String(i + 1));
}

async function emitirPackingListExcelUnico(pl, { conGanancia, lineas, draftLabel = null } = {}) {
  const icb = num(pl.precio_icb);
  const pesoByCont = pesoByContenedor(lineas);

  const proveedorRows = buildDetailRows(lineas, { mode: 'proveedor', icb, pesoByCont });
  const gananciaRows = conGanancia
    ? buildDetailRows(lineas, { mode: 'ganancia', icb, pesoByCont })
    : null;

  const wb = new ExcelJS.Workbook();
  wb.creator = 'PalmTrade ERP';
  const ws = wb.addWorksheet('Packing List', {
    views: [{ state: 'frozen', ySplit: 2 }],
  });

  const widths = [12, 22, 10, 10, 14, 10, 10, 10, 10, 9, 9, 9, 10, 10, 16, 14, 12, 8, 12, 12];
  widths.forEach((w, i) => {
    ws.getColumn(i + 1).width = w;
  });

  let r = 1;
  const tablaProveedor = writeDetailTable(
    ws,
    r,
    'Medidas con NF de proveedor',
    proveedorRows
  );
  r = tablaProveedor.nextRow + 2;

  if (conGanancia && gananciaRows) {
    const tablaGanancia = writeDetailTable(
      ws,
      r,
      'Medidas que precisamos declarar en BL y valores en Nota Fiscal',
      gananciaRows
    );
    r = tablaGanancia.nextRow + 2;
  }
  r = writeSummaryBlocks(
    ws,
    r,
    proveedorRows,
    lineas,
    pesoByCont,
    conGanancia && gananciaRows ? gananciaRows : null
  );

  if (proveedorRows.length) {
    ws.autoFilter = {
      from: { row: tablaProveedor.headerRow, column: 1 },
      to: { row: tablaProveedor.dataEnd, column: DETAIL_HEADERS.length },
    };
  }

  const fname = packingListExcelFileName(pl, draftLabel);
  const buffer = await wb.xlsx.writeBuffer();
  downloadWorkbook(buffer, fname);
  return fname;
}

/**
 * @param {object} pl - packing list (cabecera + lineas)
 * @param {{ conGanancia?: boolean }} opts
 */
export async function emitirPackingListExcel(pl, opts = {}) {
  const conGanancia = opts.conGanancia ?? !!pl.con_ganancia;
  const lineas = Array.isArray(pl.lineas) ? pl.lineas : [];
  const variosDrafts = String(pl.modo_draft || '').toLowerCase() === 'varios';
  if (!variosDrafts) {
    return emitirPackingListExcelUnico(pl, { conGanancia, lineas });
  }

  const drafts = draftOptions(pl.cantidad_drafts);
  const draftLabels = Array.isArray(opts.draftLabels)
    ? opts.draftLabels.map(normalizeDraftLabel).filter(Boolean)
    : [];
  const allowedDrafts = new Set(drafts);
  const draftsSeleccionados = draftLabels.filter((draft) => allowedDrafts.has(draft));
  const draftsAEmitir = draftsSeleccionados.length ? draftsSeleccionados : drafts;
  const fileNames = [];
  for (const draft of draftsAEmitir) {
    const lineasDraft = lineas.filter(
      (ln) => (normalizeDraftLabel(ln.draft_label) || drafts[0]) === draft
    );
    if (!lineasDraft.length) continue;
    const fname = await emitirPackingListExcelUnico(pl, {
      conGanancia,
      lineas: lineasDraft,
      draftLabel: draft,
    });
    fileNames.push(fname);
  }
  return fileNames;
}

export { fmtDec, dimStr, num as parsePackingNum, buildDetailRows, medidasFromLinea };
